import {
  BloomLevels,
  ICheckedStudentExam,
  ICheckedStudentExamRecord,
  QuestionTypes,
} from '@taleemabad/db';
import {
  CategoryData,
  ResultsData,
  StudentExamData,
  TypographyVariant,
} from '@taleemabad/dsm/components';

interface StudentResult {
  name: string;
  percentage: number;
}

interface ProficiencyLevelData {
  percentage: number;
  students: StudentResult[];
}

export interface ClassProficiencyResultsData {
  categories: string[];
  series: CategoryData[];
  proficiencyData: {
    [key: string]: ProficiencyLevelData;
  };
}

enum ProficiencyLevel {
  NeedsImprovement = 'Needs Improvement',
  Average = 'Average',
  Good = 'Good',
  Excellent = 'Excellent',
}

export class StudentDataTransformer {
  // using an array of objects allows us to easily adjust ranges or add new levels in the future.
  private static PROFICIENCY_LEVELS = [
    {
      name: ProficiencyLevel.NeedsImprovement,
      predicate: (p: number) => p <= 40,
    },
    {
      name: ProficiencyLevel.Average,
      predicate: (p: number) => p > 40 && p <= 60,
    },
    {
      name: ProficiencyLevel.Good,
      predicate: (p: number) => p > 60 && p <= 80,
    },
    {
      name: ProficiencyLevel.Excellent,
      predicate: (p: number) => p > 80,
    },
  ];

  /**
   * calculates proficiency levels of students based on their exam results.
   * this function aggregates student performance data to provide insights into overall class proficiency.
   *
   * @param exams - list of student exams with their scores.
   * @returns an object containing categories for charting, series data, and detailed proficiency data.
   */
  static async calculateClassProficiencyResultsForExams(
    exams: ICheckedStudentExam[],
  ): Promise<ClassProficiencyResultsData> {
    const totalStudents = exams.length;

    const proficiencyLevels: {
      [key in ProficiencyLevel]: ProficiencyLevelData;
    } = {
      [ProficiencyLevel.NeedsImprovement]: {
        percentage: 0,
        students: [],
      },
      [ProficiencyLevel.Average]: {
        percentage: 0,
        students: [],
      },
      [ProficiencyLevel.Good]: {
        percentage: 0,
        students: [],
      },
      [ProficiencyLevel.Excellent]: {
        percentage: 0,
        students: [],
      },
    };

    for (const exam of exams) {
      const percentage = this.calculateStudentPercentage(exam);

      const proficiencyLevel = this.getProficiencyLevel(percentage);

      const studentName = exam.studentName || 'Unknown';

      // add the student's result to the appropriate proficiency level.
      proficiencyLevels[proficiencyLevel].students.push({
        name: studentName,
        percentage: Math.round(percentage),
      });
    }

    // after categorizing all students, compute the percentage distribution across proficiency levels.
    this.calculateProficiencyPercentages(proficiencyLevels, totalStudents);

    // prepare data for charting
    const categories = [
      ProficiencyLevel.NeedsImprovement,
      ProficiencyLevel.Average,
      ProficiencyLevel.Good,
      ProficiencyLevel.Excellent,
    ];

    const data = categories.map(
      (category) => proficiencyLevels[category].percentage,
    );

    const series: CategoryData[] = [
      {
        name: 'Proficiency Level',
        data,
      },
    ];

    return {
      categories,
      series,
      proficiencyData: proficiencyLevels,
    };
  }

  private static calculateStudentPercentage(exam: ICheckedStudentExam): number {
    const totalMarks = parseFloat(exam.totalMarks || '0');
    const obtainedMarks = parseFloat(exam.obtainedMarks || '0');

    return totalMarks > 0 ? (obtainedMarks / totalMarks) * 100 : 0;
  }

  private static getProficiencyLevel(percentage: number): ProficiencyLevel {
    for (const level of this.PROFICIENCY_LEVELS) {
      if (level.predicate(percentage)) {
        return level.name;
      }
    }
    return ProficiencyLevel.NeedsImprovement;
  }

  private static calculateProficiencyPercentages(
    proficiencyLevels: { [key in ProficiencyLevel]: ProficiencyLevelData },
    totalStudents: number,
  ): void {
    for (const level in proficiencyLevels) {
      const numStudents =
        proficiencyLevels[level as ProficiencyLevel].students.length;
      // compute the percentage of students in this proficiency level, rounding to whole numbers for consistency in presentation.
      const percentage =
        totalStudents > 0 ? (numStudents / totalStudents) * 100 : 0;
      proficiencyLevels[level as ProficiencyLevel].percentage =
        Math.round(percentage);
    }
  }

  static groupExamsByQuestionTags(
    exams: ICheckedStudentExam[],
  ): Record<BloomLevels, number[]> {
    const questionTagsData: Record<BloomLevels, number[]> = Object.values(
      BloomLevels,
    ).reduce(
      (acc, level) => {
        acc[level] = [];
        return acc;
      },
      {} as Record<BloomLevels, number[]>,
    );

    exams.forEach((exam) => {
      exam.questionAnswers?.forEach((qa) => {
        const obtained = parseFloat(qa.obtainedMarks);
        const total = parseFloat(qa.marks);
        if (
          Number.isFinite(obtained) &&
          Number.isFinite(total) &&
          total !== 0
        ) {
          const percentage = (obtained / total) * 100;
          if (qa.questionTags && questionTagsData[qa.questionTags]) {
            questionTagsData[qa.questionTags].push(percentage);
          }
        }
      });
    });

    return questionTagsData;
  }

  /**
   * calculates the percentage distribution of students across predefined proficiency levels for each skill.
   *
   * by categorizing student performance into fixed proficiency levels, we can provide meaningful insights
   * into areas where students excel or need improvement. This helps in targeted interventions.
   *
   * @param bloomLevelsData - mapping of skills to arrays of student percentage scores.
   * @returns object containing categories (skills) and series data for each proficiency level.
   */
  static async calculateSkillStatistics(
    bloomLevelsData: Record<BloomLevels, number[]>,
  ): Promise<{
    categories: string[];
    series: CategoryData[];
  }> {
    type ProficiencyCounts = Record<ProficiencyLevel, number>;
    type ProficiencyPercentages = Record<BloomLevels, ProficiencyCounts>;
    const proficiencyPercentagesPerSkill = {} as ProficiencyPercentages;

    // initialize skills array based on keys present in bloomLevelsData, e.g.
    // we might not have questions for "reading" category hence we don't need to show its bar in the chart and should filter it out
    const skills: BloomLevels[] = Object.keys(bloomLevelsData).filter(
      (key) => bloomLevelsData[key as BloomLevels].length > 0,
    ) as BloomLevels[];
    const categories = skills;

    for (const skill of skills) {
      // initialize proficiency counts for the current skill i.e. reading, writing, listening, speaking
      const proficiencyCounts = this.initializeProficiencyCounts();

      const data = bloomLevelsData[skill];
      const totalCount = data.length || 1;

      // iterating over each percentage score se we can get the proficiency level i.e. Needs Improvement, Average, Good, Excellent
      this.computeProficiencyCountsForSkill(data, proficiencyCounts);

      // convert counts to percentages and store percentages for the current skill
      proficiencyPercentagesPerSkill[skill] =
        this.computeProficiencyPercentages(proficiencyCounts, totalCount);
    }

    // prepare the series data for charting
    // [
    //     {
    //         "name": "Needs Improvement",
    //         "data": [
    //             25, // reading
    //             54, // writing
    //             5,  // listening
    //             2  // speaking
    //         ]
    //     },
    // and so on for Average, Good, Excellent
    //     ...
    // ]
    const series: CategoryData[] = this.PROFICIENCY_LEVELS.map((pl) => ({
      name: pl.name,
      data: skills.map(
        (skill) => proficiencyPercentagesPerSkill[skill][pl.name],
      ),
    }));

    return {
      categories,
      series,
    };
  }

  private static initializeProficiencyCounts(): Record<
    ProficiencyLevel,
    number
  > {
    return {
      [ProficiencyLevel.NeedsImprovement]: 0,
      [ProficiencyLevel.Average]: 0,
      [ProficiencyLevel.Good]: 0,
      [ProficiencyLevel.Excellent]: 0,
    };
  }

  /**
   * computes the counts of students in each proficiency level for a given skill.
   *
   * @param data - array of percentage scores for the skill.
   * @param proficiencyCounts - object to accumulate counts per proficiency level.
   */
  private static computeProficiencyCountsForSkill(
    data: number[],
    proficiencyCounts: Record<ProficiencyLevel, number>,
  ): void {
    data.forEach((percentage) => {
      const level = this.getProficiencyLevel(percentage);
      proficiencyCounts[level]++;
    });
  }

  /**
   * computes the percentages of students in each proficiency level for a given skill.
   *
   * @param counts - counts of students per proficiency level.
   * @param totalCount - total number of students.
   * @returns an object with percentages per proficiency level.
   */
  private static computeProficiencyPercentages(
    counts: Record<ProficiencyLevel, number>,
    totalCount: number,
  ): Record<ProficiencyLevel, number> {
    const percentages = {} as Record<ProficiencyLevel, number>;

    for (const level of Object.values(ProficiencyLevel)) {
      const count = counts[level];
      percentages[level] = Math.round((count / totalCount) * 100);
    }

    return percentages;
  }

  static transformExamData = (
    data: ICheckedStudentExamRecord[],
  ): StudentExamData[] => {
    const transformedData = data?.map((item) => {
      const totalMarks = parseFloat(item.totalMarks ?? '0');

      let totalObtainedMarks = 0;
      if (item.questionAnswers) {
        totalObtainedMarks = item.questionAnswers.reduce((acc, qa) => {
          const questionMarks =
            qa.teacherMarks != undefined
              ? Number(qa.teacherMarks)
              : Number(qa.obtainedMarks ?? 0);

          return acc + Number(questionMarks);
        }, 0);
      }

      const percentage = (totalObtainedMarks / totalMarks) * 100;

      return {
        examId: item.id?.toString() ?? item.uuid ?? '',
        name: item.studentName ?? '--',
        position: 0,
        percentage: isNaN(percentage) ? 0 : percentage,
        obtainedMarks: totalObtainedMarks,
        totalMarks,
      };
    });

    transformedData.sort((a, b) => b.percentage - a.percentage);

    let position = 1;
    let lastAssignedPosition = 1;

    // if 2 or more students have the same percentage, they should have the same position
    // and the next student should have the next position
    for (let i = 0; i < transformedData.length; i++) {
      if (i === 0) {
        transformedData[i].position = position;
      } else if (
        transformedData[i].percentage !== transformedData[i - 1].percentage
      ) {
        position = lastAssignedPosition + 1;
        transformedData[i].position = position;
      } else {
        transformedData[i].position = transformedData[i - 1].position;
      }
      lastAssignedPosition = transformedData[i].position;
    }

    return transformedData;
  };

  static readonly getTextColor = (
    obtainedMarks: number,
    totalMarks: number,
    teacherMarks?: number | null,
  ): string => {
    const marks = teacherMarks ?? obtainedMarks;
    if (marks === totalMarks) return 'textBodyMediumSecondary40';
    if (marks === 0) return 'textBodyMediumError';
    return 'textBodyMediumBlue400';
  };

  static readonly transformResultDetails = (
    examRecords: ICheckedStudentExamRecord[],
    selectedExamId: number,
    questionType: string,
  ): ResultsData[] => {
    if (!examRecords?.length) return [];

    const matchingResultDetails = examRecords.find(
      (result) => result.id === selectedExamId,
    );

    if (!matchingResultDetails?.questionAnswers?.length) return [];

    return matchingResultDetails.questionAnswers
      .filter((item) => {
        if (item.groupType === QuestionTypes.Comprehension) {
          return questionType === QuestionTypes.Comprehension;
        }
        return item.type === questionType;
      })
      .map((item) => {
        const obtainedMarks = Number(item.obtainedMarks ?? '');
        const totalMarks = Number(item.marks ?? '');
        const teacherMarksValue =
          item.teacherMarks !== undefined ? Number(item.teacherMarks) : null;
        const marks = {
          obtained: teacherMarksValue ?? obtainedMarks,
          total: totalMarks,
        };

        let correctAnswerText = item.reasoning || 'N/A';

        if (marks.obtained === 0) {
          correctAnswerText = 'Incorrect answer';
        } else if (marks.obtained !== marks.total) {
          correctAnswerText = 'Partially correct answer';
        }

        return {
          id: item.id,
          type: item.type,
          question: item.question,
          teacherMarks: item.teacherMarks,
          marks,
          attemptedAnswer: `Student's answer: ${item.answer || 'N/A'}`,
          correctAnswer: correctAnswerText,
          correctAnswerVariant: StudentDataTransformer.getTextColor(
            obtainedMarks,
            totalMarks,
            teacherMarksValue,
          ) as TypographyVariant,
        };
      });
  };
}
